import { Participant, Activity } from '../models/types';
import { formatDate, formatDateRange } from './dateFormatter';

/**
 * Generates HTML for a list of participants
 * @param participants The participants
 * @returns HTML string
 */
export function generateParticipantListHtml(participants: Participant[]): string {
  if (!participants || participants.length === 0) {
    return '<p>No participants</p>';
  }
  
  return participants.map(participant => {
    let html = `<li><strong>${participant.name}</strong>`;
    
    if (participant.department) {
      html += ` - ${participant.department}`;
    }
    
    if (participant.role) {
      html += ` (${participant.role})`;
    }
    
    if (participant.email) {
      html += ` - <a href="mailto:${participant.email}">${participant.email}</a>`;
    }
    
    html += '</li>';
    return html;
  }).join('\n');
}

/**
 * Generates HTML for a list of activities
 * @param activities The activities
 * @returns HTML string
 */
export function generateActivitiesHtml(activities: Activity[]): string {
  if (!activities || activities.length === 0) {
    return '<tr><td colspan="4">No activities</td></tr>';
  }
  
  return activities.map(activity => {
    let html = '<tr>';
    html += `<td>${activity.title}</td>`;
    html += `<td>${formatDate(activity.startDate)}</td>`;
    html += `<td>${activity.endDate ? formatDate(activity.endDate) : '-'}</td>`;
    html += `<td>${activity.description || '-'}</td>`;
    html += '</tr>';
    return html;
  }).join('\n');
}

/**
 * Generates plain text for a list of participants
 * @param participants The participants
 * @returns Plain text string
 */
export function generateParticipantListText(participants: Participant[]): string {
  if (!participants || participants.length === 0) {
    return 'No participants';
  }
  
  return participants.map(participant => {
    let text = `- ${participant.name}`;
    
    if (participant.department) {
      text += ` - ${participant.department}`;
    }
    
    if (participant.role) {
      text += ` (${participant.role})`;
    }
    
    if (participant.email) {
      text += ` - ${participant.email}`;
    }
    
    return text;
  }).join('\n');
}

/**
 * Generates plain text for a list of activities
 * @param activities The activities
 * @returns Plain text string
 */
export function generateActivitiesText(activities: Activity[]): string {
  if (!activities || activities.length === 0) {
    return 'No activities';
  }
  
  return activities.map(activity => {
    let text = `- ${activity.title}: `;
    
    if (activity.endDate) {
      text += formatDateRange(activity.startDate, activity.endDate);
    } else {
      text += formatDate(activity.startDate);
    }
    
    if (activity.description) {
      text += ` - ${activity.description}`;
    }
    
    return text;
  }).join('\n');
}

/**
 * Generates a Fraport-branded HTML email template
 * @param title The email title
 * @param content The email content
 * @returns HTML string
 */
export function generateFraportEmailTemplate(title: string, content: string): string {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
    }
    p {
      margin: 10px 0;
    }
    .header {
      background-color: #ffffff;
      color: #003366;
      padding: 20px;
      text-align: center;
    }
    .logo {
      max-width: 200px;
      height: auto;
    }
    .content {
      padding: 10px;
      background-color: #ffffff;
    }
    .footer {
      background-color: #f5f5f5;
      padding: 10px 20px;
      font-size: 12px;
      color: #666;
      text-align: center;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 20px 0;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    th {
      background-color: #f2f2f2;
      font-weight: bold;
    }
    h1, h2, h3 {
      color: #003366;
    }
    a {
      color: #0066cc;
      text-decoration: none;
    }
    a:hover {
      text-decoration: underline;
    }
    /* List styles */
    ul {
      margin: 15px 0;
      padding-left: 20px;
      list-style-type: disc;
    }
    ol {
      margin: 15px 0;
      padding-left: 20px;
      list-style-type: decimal;
    }
    li {
      margin-bottom: 8px;
      padding-left: 5px;
    }
    /* End list styles */
    
    /* Section spacing */
    .section {
      margin-bottom: 20px;
    }
    .section-title {
      font-weight: bold;
      margin-top: 20px;
      margin-bottom: 10px;
    }
    /* End section spacing */
    .button {
      display: inline-block;
      background-color: #003366;
      color: white;
      padding: 10px 20px;
      text-decoration: none;
      border-radius: 4px;
      margin: 10px 0;
    }
  </style>
</head>
<body>
  <div class="content">
    ${content}
  </div>
  <div class="footer">
    <p>This is an automatically generated email. Please do not reply directly to this message.</p>
    <p>&copy; ${new Date().getFullYear()} Fraport AG. All rights reserved.</p>
  </div>
</body>
</html>
  `;
}
